import { Component } from "@theme/component";

/**
 * A custom element that manages a carousel.
 *
 * @extends {Component}
 */
export default class CarouselComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initCarousel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#visibilityTimer) clearInterval(this.#visibilityTimer);
  }

  getConfig() {
    const navigation = this.showNavigation
      ? {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        }
      : null;
    const pagination = this.showPagination
      ? {
          el: ".swiper-pagination",
        }
      : null;
    const scrollbar = this.showScrollbar
      ? {
          el: ".swiper-scrollbar",
        }
      : null;

    const autoplay = this.autoplay
      ? {
          delay: this.autoplayDelay,
          disableOnInteraction: this.disableOnInteraction || false,
        }
      : null;

    const speed = this.speed ? this.speed : null;
    return {
      // Needed for the `swiper-slide-visible` class, used to hide inactive slides from assistive tech.
      watchSlidesProgress: true,
      loop: this.loop,
      slidesPerView: this.slidesAuto ? "auto" : this.slidesPerView,
      spaceBetween: this.spaceBetween,
      centeredSlides: this.centeredSlides,
      initialSlide: this.initialSlide,
      autoplay: autoplay || false,
      allowTouchMove: this.allowTouchMove,
      speed: speed,
      navigation: navigation || false,
      pagination: pagination || false,
      scrollbar: scrollbar || false,
      a11y: this.enableA11y
        ? { slideLabelMessage: "slide {{index}} of {{slidesLength}}" }
        : false,
      breakpoints: {
        768: {
          slidesPerView: this.slidesAuto ? "auto" : this.slidesPerViewDesktop,
          spaceBetween: this.spaceBetweenDesktop,
        },
      },
    };
  }

  initCarousel() {
    // @ts-ignore
    const swiper = new Swiper(
      this.querySelector("[ref='swiperCarousel']"),
      this.getConfig()
    );

    if (this.autoplay) {
      swiper.on("autoplayStart", () => {
        this.paused = false;
      });
      swiper.on("autoplayStop", () => {
        this.paused = true;
      });
    }

    this.swiper = swiper;

    // Inactive (off-screen) slides must not be reachable by keyboard or screen readers.
    this.#syncSlideVisibility();
    // setTranslate fires when a move starts (and on drag / pause): keep both the slides on screen now and
    // the ones that will be, so nothing in motion is hidden early.
    swiper.on("setTranslate", () => {
      this.#syncSlideVisibility(true);
      this.#watchMovingSlides();
    });
    swiper.on("transitionEnd", () => this.#syncSlideVisibility());
    swiper.on("resize", () => this.#syncSlideVisibility());
  }

  /** @type {ReturnType<typeof setInterval> | null} */
  #visibilityTimer = null;

  /**
   * Long transitions (e.g. the smooth-loop marquee) move slides in and out of view mid-transition:
   * re-check every 300ms while the carousel is moving, then stop.
   */
  #watchMovingSlides() {
    if (this.#visibilityTimer) return;
    this.#visibilityTimer = setInterval(() => {
      this.#syncSlideVisibility();
      if (!this.swiper?.animating || !this.isConnected) {
        clearInterval(/** @type {ReturnType<typeof setInterval>} */ (this.#visibilityTimer));
        this.#visibilityTimer = null;
      }
    }, 300);
  }

  /**
   * aria-hidden + tabindex="-1" on every slide that is not in view; restore them when it comes back.
   * @param {boolean} [includeTarget] Also keep slides Swiper marks visible for the position it is moving to.
   */
  #syncSlideVisibility(includeTarget = false) {
    const swiper = this.swiper;
    if (!swiper?.el) return;

    const bounds = swiper.el.getBoundingClientRect();
    const FOCUSABLE = "a[href], button, input, select, textarea, iframe, [tabindex]";

    swiper.slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const onScreen = rect.width > 0 && rect.right > bounds.left + 1 && rect.left < bounds.right - 1;
      const visible = onScreen || (includeTarget && slide.classList.contains("swiper-slide-visible"));

      if (visible) {
        slide.removeAttribute("aria-hidden");
        slide.querySelectorAll("[data-saved-tabindex]").forEach((el) => {
          const saved = el.getAttribute("data-saved-tabindex");
          if (saved === "none") el.removeAttribute("tabindex");
          else el.setAttribute("tabindex", saved ?? "0");
          el.removeAttribute("data-saved-tabindex");
        });
      } else {
        slide.setAttribute("aria-hidden", "true");
        slide.querySelectorAll(FOCUSABLE).forEach((el) => {
          if (!el.hasAttribute("data-saved-tabindex")) {
            el.setAttribute("data-saved-tabindex", el.getAttribute("tabindex") ?? "none");
          }
          el.setAttribute("tabindex", "-1");
        });
      }
    });
  }

  pause() {
    const swiper = this.swiper;
    if (!swiper) return;

    swiper.autoplay?.stop();

    // autoplay.stop() only cancels the next slide; the running CSS transition keeps
    // going (up to `speed` ms). Freeze the wrapper where it is right now instead.
    if (swiper.animating) {
      this.frozenTarget = swiper.translate;
      swiper.setTransition(0);
      swiper.setTranslate(swiper.getTranslate());
    }
  }

  play() {
    const swiper = this.swiper;
    if (!swiper) return;

    // Finish the interrupted transition from the frozen position at the same pace.
    if (typeof this.frozenTarget === "number") {
      const target = this.frozenTarget;
      this.frozenTarget = null;

      const distance = Math.abs(target - swiper.translate);
      const step = swiper.slidesSizesGrid[swiper.activeIndex] + (parseFloat(swiper.params.spaceBetween) || 0);
      const duration = step > 0 ? Math.round((swiper.params.speed * distance) / step) : 0;

      if (distance > 0 && duration > 0) {
        swiper.setTransition(duration);
        swiper.setTranslate(target);
      } else {
        swiper.setTranslate(target);
        swiper.transitionEnd(true);
        swiper.animating = false;
      }
    }

    swiper.autoplay?.start();
  }

  toggleAutoplay() {
    if (this.paused) this.play();
    else this.pause();
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  set paused(value) {
    if (value) this.setAttribute("paused", "");
    else this.removeAttribute("paused");

    const toggle = this.refs.autoplayToggle;
    if (toggle instanceof HTMLElement) {
      toggle.setAttribute(
        "aria-label",
        (value ? toggle.dataset.playLabel : toggle.dataset.pauseLabel) ?? ""
      );
    }
  }

  get loop() {
    const loop = this.getAttribute("loop");
    if (!loop) throw new Error("Loop is required");
    return loop === "true" ? true : false;
  }

  get slidesAuto() {
    const slidesAuto = this.getAttribute("slides-auto");
    if (!slidesAuto) throw new Error("Slides auto is required");
    return slidesAuto === "true" ? true : false;
  }

  get slidesPerView() {
    const slidesPerView = this.getAttribute("slides-per-view");
    if (!slidesPerView) throw new Error("Slides per view is required");
    return Number(slidesPerView);
  }

  get slidesPerViewDesktop() {
    const slidesPerViewDesktop = this.getAttribute("slides-per-view-desktop");
    if (!slidesPerViewDesktop)
      throw new Error("Slides per view desktop is required");
    return Number(slidesPerViewDesktop);
  }

  get spaceBetween() {
    const spaceBetween = this.getAttribute("space-between");
    if (!spaceBetween) throw new Error("Space between is required");
    return Number(spaceBetween);
  }

  get spaceBetweenDesktop() {
    const spaceBetweenDesktop = this.getAttribute("space-between-desktop");
    if (!spaceBetweenDesktop)
      throw new Error("Space between desktop is required");
    return spaceBetweenDesktop;
  }

  get centeredSlides() {
    const centeredSlides = this.getAttribute("centered-slides");
    if (!centeredSlides) throw new Error("Centered slides is required");
    return centeredSlides === "true" ? true : false;
  }

  get initialSlide() {
    const initialSlide = this.getAttribute("initial-slide");
    if (!initialSlide) throw new Error("Initial slide is required");

    return Number(initialSlide);
  }

  get autoplay() {
    const autoplay = this.getAttribute("autoplay");
    if (!autoplay) throw new Error("Autoplay is required");
    return autoplay === "true" ? true : false;
  }
  get speed() {
    const speed = this.getAttribute("speed");
    if (!speed) throw new Error("Speed is required");
    return Number(speed);
  }

  get disableOnInteraction() {
    const disableOnInteraction = this.getAttribute("disable-on-interaction");
    if (!disableOnInteraction)
      throw new Error("Disable on interaction is required");
    return disableOnInteraction === "true" ? true : false;
  }

  get allowTouchMove() {
    const allowTouchMove = this.getAttribute("allow-touch-move");
    if (!allowTouchMove) throw new Error("Allow touch move is required");
    return allowTouchMove === "true" ? true : false;
  }

  get autoplayDelay() {
    const autoplayDelay = this.getAttribute("autoplay-delay");
    if (!autoplayDelay) throw new Error("Autoplay delay is required");
    return Number(autoplayDelay);
  }

  get showNavigation() {
    const showNavigation = this.getAttribute("show-navigation");
    if (!showNavigation) throw new Error("Show navigation is required");
    return showNavigation === "true" ? true : false;
  }

  get showPagination() {
    const showPagination = this.getAttribute("show-pagination");
    if (!showPagination) throw new Error("Show pagination is required");
    return showPagination === "true" ? true : false;
  }

  get showScrollbar() {
    const showScrollbar = this.getAttribute("show-scrollbar");
    if (!showScrollbar) throw new Error("Show scrollbar is required");
    return showScrollbar === "true" ? true : false;
  }

  get enableA11y() {
    return this.getAttribute("enable-a11y") !== "false";
  }
}

if (!customElements.get("carousel-component")) {
  customElements.define("carousel-component", CarouselComponent);
}
